import { z } from 'zod';
import {
  type AddProjectMemberInput,
  type OrgUser,
  OrgUserSchema,
  type ProjectId,
  type ProjectMember,
  ProjectMemberSchema,
  type UpdateProjectMemberRoleInput,
} from '@voll-intelligence/domain';
import type { HttpService } from '@web/data/services';

const ListMembersResponseSchema = z.object({ members: z.array(ProjectMemberSchema) });
const MemberResponseSchema = z.object({ member: ProjectMemberSchema });
const AssignableUsersResponseSchema = z.object({ users: z.array(OrgUserSchema) });

interface ProjectMembersRepository {
  list: (orgId: string, projectId: ProjectId) => Promise<ProjectMember[]>;
  listAssignableUsers: (orgId: string, projectId: ProjectId) => Promise<OrgUser[]>;
  add: (
    orgId: string,
    projectId: ProjectId,
    input: AddProjectMemberInput,
  ) => Promise<ProjectMember>;
  updateRole: (
    orgId: string,
    projectId: ProjectId,
    userId: string,
    input: UpdateProjectMemberRoleInput,
  ) => Promise<ProjectMember>;
  remove: (orgId: string, projectId: ProjectId, userId: string) => Promise<void>;
}

export function createProjectMembersRepository(http: HttpService): ProjectMembersRepository {
  async function list(orgId: string, projectId: ProjectId): Promise<ProjectMember[]> {
    const { members } = await http.get(`/orgs/${orgId}/projects/${projectId}/members`, {
      schema: ListMembersResponseSchema,
    });
    return members;
  }

  async function listAssignableUsers(orgId: string, projectId: ProjectId): Promise<OrgUser[]> {
    const { users } = await http.get(`/orgs/${orgId}/projects/${projectId}/assignable-users`, {
      schema: AssignableUsersResponseSchema,
    });
    return users;
  }

  async function add(
    orgId: string,
    projectId: ProjectId,
    input: AddProjectMemberInput,
  ): Promise<ProjectMember> {
    const { member } = await http.post(`/orgs/${orgId}/projects/${projectId}/members`, input, {
      schema: MemberResponseSchema,
    });
    return member;
  }

  async function updateRole(
    orgId: string,
    projectId: ProjectId,
    userId: string,
    input: UpdateProjectMemberRoleInput,
  ): Promise<ProjectMember> {
    const { member } = await http.patch(
      `/orgs/${orgId}/projects/${projectId}/members/${userId}`,
      input,
      { schema: MemberResponseSchema },
    );
    return member;
  }

  async function remove(orgId: string, projectId: ProjectId, userId: string): Promise<void> {
    await http.delete(`/orgs/${orgId}/projects/${projectId}/members/${userId}`);
  }

  return { list, listAssignableUsers, add, updateRole, remove };
}
